import useSetLoading from '@/hooks/useSetLoading';
import alertState from '@/states/alertState';
import { PaymentDetailsState } from '@/states/paymentDetailsState';
import { PaymentDetailsResponse, RefundList } from '@/types/api/mypage/paymentDetails';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import { formatYYYYMM } from '@/utils/commonFunc';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';
import { useEffect } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import Heading from '../atoms/Heading';

type Props = {
  anchor: string;
};

/**
 * 返金対象月の支払い明細一覧を取得し、PDF ダウンロードへつなぐセクション。
 * 明細公開がある月だけ表示し、存在しない場合は画面に空ブロックを残さないようにしている。
 */
const PaymentDetails = ({ anchor }: Props) => {
  const [paymentDetails, setPaymentDetails] =
    useRecoilState<PaymentDetailsResponse>(PaymentDetailsState);
  const setAlert = useSetRecoilState(alertState);
  const { startLoading, finishLoading } = useSetLoading();

  useEffect(() => {
    (async () => {
      startLoading(ApiUrl.Read.BILLING_RLS_REFUND_LIST);
      const headers = await getSigV4HeadersForGet(ApiUrl.Read.BILLING_RLS_REFUND_LIST);
      await api
        .get(ApiUrl.Read.BILLING_RLS_REFUND_LIST, headers)
        .then((res) => {
          setPaymentDetails(res.data);
        })
        .catch((error: any) => {
          if (error.response?.status === 404) {
            return;
          }
          if (isExpiredToken(error)) {
            sessionStorage.clear();
            window.location.href = url('/login');
            return;
          }
          setAlert({
            isOpen: true,
            apiType: Constants.ApiType.API_GET,
          });
        })
        .finally(() => finishLoading(ApiUrl.Read.BILLING_RLS_REFUND_LIST));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadRefundData = (targetMonth: string) => {
    (async () => {
      // ダウンロード API は base64 返却なので、一度 Blob 化してブラウザの保存動作へ変換する。
      startLoading(ApiUrl.Read.DOWNLOAD_REFUND);
      const sigV4Headers = await getSigV4HeadersForGet(
        `${ApiUrl.Read.DOWNLOAD_REFUND}/${targetMonth.slice(0, 4)}-${targetMonth.slice(4)}/${
          Constants.SubSystemId.NURO
        }`
      );
      await api
        .get(
          `${ApiUrl.Read.DOWNLOAD_REFUND}/${targetMonth.slice(0, 4)}-${targetMonth.slice(4)}/${
            Constants.SubSystemId.NURO
          }`,
          {
            headers: sigV4Headers.headers,
          }
        )
        .then(({ data }) => {
          const buffer = Buffer.from(data.data, 'base64');
          const blob = new Blob([buffer], { type: 'application/pdf' });
          const downloadUrl = URL.createObjectURL(blob);
          const download = document.createElement('a');
          download.href = downloadUrl;
          download.download = data.file_name;
          download.click();
          (window.URL || window.webkitURL).revokeObjectURL(downloadUrl);
        })
        .catch((error: any) => {
          if (isExpiredToken(error)) {
            sessionStorage.clear();
            window.location.href = url('/login');
            return;
          }
          setAlert({
            isOpen: true,
            apiType: '',
          });
        })
        .finally(() => finishLoading(ApiUrl.Read.DOWNLOAD_REFUND));
    })();
  };

  // 公開対象が無いときはセクションごと非表示にして、不要な見出しだけ残さない。
  if (paymentDetails.refund_list.length === 0) {
    return null;
  }

  // 公開対象があるときだけ一覧とダウンロード導線を表示する。
  return (
    <div className='m-box-02' id={anchor}>
      <div className='m-box-02__inner'>
        <Heading as='h2' icon='payment'>
          お支払い明細
        </Heading>
        <div className='m-table'>
          <table>
            <colgroup>
              <col width='30%' />
              <col width='30%' />
              <col width='30%' />
            </colgroup>
            <tbody>
              <tr>
                <th className='m-table__th'>お支払い年月</th>
                <th colSpan={2} className='m-table__th'>
                  お支払い金額（税込）
                </th>
              </tr>
              {paymentDetails.refund_list.map((item: RefundList) => {
                return (
                  <tr key={item.target_ym}>
                    <td className='m-table__td' data-testid='target_ym'>
                      {formatYYYYMM(item.target_ym)}
                    </td>
                    <td className='m-table__td' data-testid='total_price_tax_include'>
                      {numberFormat(item.total_price_tax_include)}
                    </td>
                    <td className='m-table__td m-carousel__linkTxt'>
                      <button
                        className='m-carousel__linkTxt'
                        onClick={() => downloadRefundData(item.target_ym)}
                        data-testid='download_link'
                        aria-label='明細を表示する'
                      >
                        明細を表示
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// 画面文言に合わせて金額を日本円の表示形式へ統一する。
const numberFormat = (num: number): string => {
  return `${num.toLocaleString('ja-JP', { style: 'currency', currency: 'JPY' })}円`;
};

export default PaymentDetails;
